import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import { HOME_ROUTE } from '../../../lib/constants/route';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import get_action_proposals_api_call from '../../../lib/api/actions/get-action-proposals';
import approve_action_api_call from '../../../lib/api/actions/approve-action';
import deny_action_api_call from '../../../lib/api/actions/deny-action';
import { ActionProposal } from '../../../lib/api/actions/action-types';
import { setMessage } from '../../../store/reducers/appSlice';
import ActionProposalCard from './ActionProposalCard';
import '../actions.scss';

const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

const ActionQueue = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [proposals, setProposals] = useState<ActionProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('pending_review');
  const [platformFilter, setPlatformFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const statusTabs = [
    { label: t('All'), value: '' },
    { label: t('PendingReview'), value: 'pending_review' },
    { label: t('Approved'), value: 'approved' },
    { label: t('Executing'), value: 'executing' },
  ];

  const fetchProposals = useCallback(async () => {
    try {
      const result = await get_action_proposals_api_call({
        status: statusFilter || undefined,
        platform: platformFilter || undefined,
        page: currentPage,
        per_page: 20,
      });

      if (result) {
        setProposals(result.data);
        setTotalPages(result.last_page);
      } else {
        setProposals([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Failed to load action proposals:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, platformFilter, currentPage]);

  useEffect(() => {
    setLoading(true);
    setCurrentPage(1);
  }, [statusFilter, platformFilter]);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  // Auto-refresh
  useEffect(() => {
    refreshTimer.current = setInterval(() => {
      fetchProposals();
    }, AUTO_REFRESH_INTERVAL);

    return () => {
      if (refreshTimer.current) {
        clearInterval(refreshTimer.current);
      }
    };
  }, [fetchProposals]);

  const handleApprove = async (id: string, notes?: string) => {
    const result = await approve_action_api_call(id, { notes });
    if (result) {
      dispatch(
        setMessage({
          message: t('ActionApprovedSuccessfully'),
          messageType: 'success',
        })
      );
      fetchProposals();
    } else {
      dispatch(
        setMessage({
          message: t('FailedToApproveAction'),
          messageType: 'error',
        })
      );
    }
  };

  const handleDeny = async (id: string, reason?: string) => {
    const result = await deny_action_api_call(id, { reason });
    if (result) {
      dispatch(
        setMessage({
          message: t('ActionDeniedSuccessfully'),
          messageType: 'success',
        })
      );
      fetchProposals();
    } else {
      dispatch(
        setMessage({
          message: t('FailedToDenyAction'),
          messageType: 'error',
        })
      );
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages: number[] = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }

    return (
      <div className='action-pagination'>
        <button
          className='action-pagination__btn'
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
        >
          {t('Previous')}
        </button>
        {pages.map((page) => (
          <button
            key={page}
            className={`action-pagination__btn ${
              page === currentPage ? 'action-pagination__btn--active' : ''
            }`}
            onClick={() => setCurrentPage(page)}
          >
            {page}
          </button>
        ))}
        <button
          className='action-pagination__btn'
          disabled={currentPage === totalPages}
          onClick={() =>
            setCurrentPage((prev) => Math.min(totalPages, prev + 1))
          }
        >
          {t('Next')}
        </button>
      </div>
    );
  };

  return (
    <SubPageInnerContentHolder
      title={t('ActionQueue')}
      backBtnLink={HOME_ROUTE}
    >
      {/* Filters */}
      <div className='action-queue-filters'>
        <div className='action-queue-filters__status-group'>
          {statusTabs.map((tab) => (
            <button
              key={tab.value}
              className={`action-queue-filters__filter-btn ${
                statusFilter === tab.value
                  ? 'action-queue-filters__filter-btn--active'
                  : ''
              }`}
              onClick={() => setStatusFilter(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <select
          className='action-queue-filters__platform-select'
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value)}
        >
          <option value=''>{t('AllPlatforms')}</option>
          <option value='facebook_ads'>Facebook Ads</option>
          <option value='google_ads'>Google Ads</option>
          <option value='tiktok_ads'>TikTok Ads</option>
          <option value='linkedin_ads'>LinkedIn Ads</option>
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <LoadingSpinner customLoadingMsg={t('LoadingActionProposals')} />
        </div>
      ) : proposals.length === 0 ? (
        <div className='action-empty-state'>
          <p className='action-empty-state__text'>
            {t('NoActionProposalsFound')}
          </p>
        </div>
      ) : (
        <>
          {proposals.map((proposal) => (
            <ActionProposalCard
              key={proposal.id}
              proposal={proposal}
              onApprove={handleApprove}
              onDeny={handleDeny}
              showActions={true}
            />
          ))}
          {renderPagination()}
        </>
      )}
    </SubPageInnerContentHolder>
  );
};

export default ActionQueue;
